import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Shuffle, Zap, CheckCircle2, Hash } from 'lucide-react';

interface GenerationStats {
  numbersGenerated: number;
  largestNumber: number;
  smallestNumber: number;
  totalTime: number;
  averageTime: number;
  lastMethod: string;
}

interface RandomResult {
  numbers: number[];
  method: string;
  count: number;
  processingTime: number;
  timestamp: Date;
  statistics?: {
    min: number;
    max: number;
    mean: number;
    median: number;
    mode: number[];
    standardDeviation: number;
    variance: number;
    range: number;
  };
  distribution?: { [key: string]: number };
}

const RandomNumberGenerator: React.FC = () => {
  const [count, setCount] = useState<string>('10');
  const [minValue, setMinValue] = useState<string>('1');
  const [maxValue, setMaxValue] = useState<string>('100');
  const [generationMethod, setGenerationMethod] = useState<'range' | 'dice' | 'coin' | 'custom' | 'gaussian'>('range');
  const [allowDuplicates, setAllowDuplicates] = useState<boolean>(true);
  const [showStatistics, setShowStatistics] = useState<boolean>(false);
  const [showDistribution, setShowDistribution] = useState<boolean>(false);
  const [customValues, setCustomValues] = useState<string>('');
  const [diceType, setDiceType] = useState<string>('6');
  const [result, setResult] = useState<RandomResult | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<GenerationStats>({
    numbersGenerated: 0,
    largestNumber: 0,
    smallestNumber: Infinity,
    totalTime: 0,
    averageTime: 0,
    lastMethod: ''
  });
  const [notification, setNotification] = useState<string>('');
  const [history, setHistory] = useState<RandomResult[]>([]);
  const [seed, setSeed] = useState<string>('');

  // Seeded random number generator (Linear Congruential Generator)
  const createSeededRandom = (seed: number) => {
    let state = seed;
    return () => {
      state = (state * 1664525 + 1013904223) % Math.pow(2, 32);
      return state / Math.pow(2, 32);
    };
  };

  const calculateStatistics = (numbers: number[]) => {
    if (numbers.length === 0) return undefined;

    const sorted = [...numbers].sort((a, b) => a - b);
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    const mean = numbers.reduce((sum, n) => sum + n, 0) / numbers.length;
    
    const median = sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)];

    // Calculate mode
    const frequency: { [key: number]: number } = {};
    numbers.forEach(n => frequency[n] = (frequency[n] || 0) + 1);
    const maxFreq = Math.max(...Object.values(frequency));
    const mode = Object.keys(frequency)
      .filter(k => frequency[parseInt(k)] === maxFreq)
      .map(k => parseInt(k));

    // Calculate standard deviation and variance
    const variance = numbers.reduce((sum, n) => sum + Math.pow(n - mean, 2), 0) / numbers.length;
    const standardDeviation = Math.sqrt(variance);

    return {
      min,
      max,
      mean: parseFloat(mean.toFixed(2)),
      median,
      mode,
      standardDeviation: parseFloat(standardDeviation.toFixed(2)),
      variance: parseFloat(variance.toFixed(2)),
      range: max - min
    };
  };

  const calculateDistribution = (numbers: number[]) => {
    const distribution: { [key: string]: number } = {};
    numbers.forEach(n => {
      const key = n.toString();
      distribution[key] = (distribution[key] || 0) + 1;
    });
    return distribution;
  };

  // Box-Muller transform for Gaussian distribution
  const generateGaussian = (mean: number = 0, stdDev: number = 1) => {
    let u = 0, v = 0;
    while(u === 0) u = Math.random(); // Converting [0,1) to (0,1)
    while(v === 0) v = Math.random();
    
    const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    return z * stdDev + mean;
  };

  const generateRandomNumbers = useCallback((): RandomResult | null => {
    const startTime = performance.now();
    
    try {
      const num = parseInt(count);
      if (isNaN(num) || num <= 0 || num > 10000) {
        return null;
      }

      let numbers: number[] = [];
      const randomFunc = seed ? createSeededRandom(parseInt(seed) || Date.now()) : Math.random;

      switch (generationMethod) {
        case 'range':
          const min = parseInt(minValue);
          const max = parseInt(maxValue);
          if (isNaN(min) || isNaN(max) || min >= max) return null;

          if (!allowDuplicates && (max - min + 1) < num) {
            return null; // Not enough unique numbers in range
          }

          if (allowDuplicates) {
            for (let i = 0; i < num; i++) {
              numbers.push(Math.floor(randomFunc() * (max - min + 1)) + min);
            }
          } else {
            const available = Array.from({ length: max - min + 1 }, (_, i) => min + i);
            for (let i = 0; i < num; i++) {
              const index = Math.floor(randomFunc() * available.length);
              numbers.push(available.splice(index, 1)[0]);
            }
          }
          break;

        case 'dice':
          const sides = parseInt(diceType);
          if (isNaN(sides) || sides < 2) return null;

          for (let i = 0; i < num; i++) {
            numbers.push(Math.floor(randomFunc() * sides) + 1);
          }
          break;

        case 'coin':
          for (let i = 0; i < num; i++) {
            numbers.push(randomFunc() < 0.5 ? 0 : 1);
          }
          break;

        case 'custom':
          const customNums = customValues.split(',')
            .map(v => parseFloat(v.trim()))
            .filter(v => !isNaN(v));
          
          if (customNums.length === 0) return null;

          if (!allowDuplicates && customNums.length < num) {
            return null;
          }

          if (allowDuplicates) {
            for (let i = 0; i < num; i++) {
              numbers.push(customNums[Math.floor(randomFunc() * customNums.length)]);
            }
          } else {
            const available = [...customNums];
            for (let i = 0; i < num; i++) {
              const index = Math.floor(randomFunc() * available.length);
              numbers.push(available.splice(index, 1)[0]);
            }
          }
          break;

        case 'gaussian':
          const meanVal = parseFloat(minValue) || 0;
          const stdDevVal = parseFloat(maxValue) || 1;
          
          for (let i = 0; i < num; i++) {
            numbers.push(parseFloat(generateGaussian(meanVal, stdDevVal).toFixed(2)));
          }
          break;

        default:
          return null;
      }

      const endTime = performance.now();
      const processingTime = endTime - startTime;

      return {
        numbers,
        method: generationMethod,
        count: numbers.length,
        processingTime,
        timestamp: new Date(),
        statistics: showStatistics ? calculateStatistics(numbers) : undefined,
        distribution: showDistribution ? calculateDistribution(numbers) : undefined
      };
    } catch (error) {
      return null;
    }
  }, [count, minValue, maxValue, generationMethod, allowDuplicates, showStatistics, showDistribution, customValues, diceType, seed]);

  const performGeneration = useCallback((): void => {
    const randomResult = generateRandomNumbers();
    if (randomResult) {
      setResult(randomResult);
      setHistory(prev => [randomResult, ...prev.slice(0, 9)]);
      
      const minInSequence = Math.min(...randomResult.numbers);
      const maxInSequence = Math.max(...randomResult.numbers);
      
      setStats(prev => ({
        numbersGenerated: prev.numbersGenerated + randomResult.numbers.length,
        largestNumber: Math.max(maxInSequence, prev.largestNumber),
        smallestNumber: Math.min(minInSequence, prev.smallestNumber === Infinity ? minInSequence : prev.smallestNumber),
        totalTime: prev.totalTime + randomResult.processingTime,
        averageTime: (prev.totalTime + randomResult.processingTime) / Math.max(1, prev.numbersGenerated + randomResult.numbers.length),
        lastMethod: randomResult.method
      }));
    } else {
      setResult(null);
    }
  }, [generateRandomNumbers]);

  useEffect(() => {
    if (count && (
      (generationMethod === 'range' && minValue && maxValue) ||
      (generationMethod === 'dice' && diceType) ||
      generationMethod === 'coin' ||
      (generationMethod === 'custom' && customValues) ||
      (generationMethod === 'gaussian' && minValue && maxValue)
    )) {
      performGeneration();
    }
  }, [count, minValue, maxValue, generationMethod, allowDuplicates, showStatistics, showDistribution, customValues, diceType, seed, performGeneration]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    if (!result) return;
    
    let textToCopy = `Random Numbers (${result.method}):\n`;
    textToCopy += result.numbers.join(', ');
    
    if (result.statistics) {
      textToCopy += '\n\nStatistics:\n';
      textToCopy += `Min: ${result.statistics.min}\n`;
      textToCopy += `Max: ${result.statistics.max}\n`;
      textToCopy += `Mean: ${result.statistics.mean}\n`;
      textToCopy += `Median: ${result.statistics.median}\n`;
      textToCopy += `Standard Deviation: ${result.statistics.standardDeviation}\n`;
    }
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      showNotification('Numbers copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadNumbers = (): void => {
    if (!result) return;
    
    let content = `Random Number Generation Report\n`;
    content += `Generated: ${result.timestamp.toLocaleString()}\n`;
    content += `Method: ${result.method}\n`;
    content += `Count: ${result.count} numbers\n`;
    content += `Processing Time: ${result.processingTime.toFixed(2)}ms\n\n`;
    
    content += `Numbers:\n${result.numbers.join(', ')}\n\n`;
    
    if (result.statistics) {
      content += `Statistics:\n`;
      content += `- Minimum: ${result.statistics.min}\n`;
      content += `- Maximum: ${result.statistics.max}\n`;
      content += `- Mean: ${result.statistics.mean}\n`;
      content += `- Median: ${result.statistics.median}\n`;
      content += `- Mode: ${result.statistics.mode.join(', ')}\n`;
      content += `- Standard Deviation: ${result.statistics.standardDeviation}\n`;
      content += `- Variance: ${result.statistics.variance}\n`;
      content += `- Range: ${result.statistics.range}\n\n`;
    }
    
    if (result.distribution) {
      content += `Distribution:\n`;
      Object.entries(result.distribution)
        .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
        .forEach(([value, frequency]) => {
          content += `${value}: ${frequency} times\n`;
        });
      content += `\n`;
    }
    
    content += `Session Statistics:\n`;
    content += `- Total Numbers Generated: ${stats.numbersGenerated}\n`;
    content += `- Largest Number: ${stats.largestNumber}\n`;
    content += `- Smallest Number: ${stats.smallestNumber}\n`;
    content += `- Average Generation Time: ${stats.averageTime.toFixed(2)}ms\n`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'random-numbers.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Numbers downloaded!');
  };

  const clearAll = (): void => {
    setCount('10');
    setMinValue('1');
    setMaxValue('100');
    setCustomValues('');
    setDiceType('6');
    setSeed('');
    setAllowDuplicates(true);
    setShowStatistics(false);
    setShowDistribution(false);
    setResult(null);
    setHistory([]);
    setStats({
      numbersGenerated: 0,
      largestNumber: 0,
      smallestNumber: Infinity,
      totalTime: 0,
      averageTime: 0,
      lastMethod: ''
    });
    showNotification('Generator reset!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleGeneration = (): void => {
    setGenerationMethod('range');
    setCount('20');
    setMinValue('1');
    setMaxValue('100');
    setShowStatistics(true);
    setShowDistribution(true);
    showNotification('Sample generation loaded!');
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg">
              <Shuffle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Random Number Generator</h1>
              <p className="text-gray-300 text-sm">Advanced random number generation with statistical analysis</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Generation Method Selector */}
        <div className="mb-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Generation Method</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <button
              onClick={() => setGenerationMethod('range')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                generationMethod === 'range'
                  ? 'border-purple-500 bg-purple-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Range</div>
              <div className="text-sm opacity-75">Numbers within min-max</div>
            </button>
            
            <button
              onClick={() => setGenerationMethod('dice')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                generationMethod === 'dice'
                  ? 'border-purple-500 bg-purple-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Dice Roll</div>
              <div className="text-sm opacity-75">Simulate dice rolls</div>
            </button>
            
            <button
              onClick={() => setGenerationMethod('coin')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                generationMethod === 'coin'
                  ? 'border-purple-500 bg-purple-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Coin Flip</div>
              <div className="text-sm opacity-75">0 (tails) or 1 (heads)</div>
            </button>
            
            <button
              onClick={() => setGenerationMethod('custom')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                generationMethod === 'custom'
                  ? 'border-purple-500 bg-purple-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Custom Set</div>
              <div className="text-sm opacity-75">From your list</div>
            </button>
            
            <button
              onClick={() => setGenerationMethod('gaussian')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                generationMethod === 'gaussian'
                  ? 'border-purple-500 bg-purple-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Gaussian</div>
              <div className="text-sm opacity-75">Normal distribution</div>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Generation Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.numbersGenerated}</div>
                <div className="text-gray-300 text-sm">Numbers</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.averageTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Avg Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-xl font-bold text-green-400">{stats.largestNumber}</div>
                <div className="text-gray-300 text-sm">Largest</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-xl font-bold text-blue-400">{stats.smallestNumber === Infinity ? 0 : stats.smallestNumber}</div>
                <div className="text-gray-300 text-sm">Smallest</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{history.length}</div>
                <div className="text-gray-300 text-sm">History</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Parameters</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadSampleGeneration}
                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Zap className="w-4 h-4" />
                </button>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4 flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Number of Values (1-10000)
                </label>
                <input
                  type="number"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  min="1"
                  max="10000"
                  placeholder="Enter count"
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                />
              </div>

              {generationMethod === 'range' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Min Value</label>
                    <input
                      type="number"
                      value={minValue}
                      onChange={(e) => setMinValue(e.target.value)}
                      placeholder="Minimum"
                      className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Max Value</label>
                    <input
                      type="number"
                      value={maxValue}
                      onChange={(e) => setMaxValue(e.target.value)}
                      placeholder="Maximum"
                      className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </div>
                </div>
              )}

              {generationMethod === 'dice' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Dice Sides</label>
                  <select
                    value={diceType}
                    onChange={(e) => setDiceType(e.target.value)}
                    className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  >
                    <option value="4">4-sided (D4)</option>
                    <option value="6">6-sided (D6)</option>
                    <option value="8">8-sided (D8)</option>
                    <option value="10">10-sided (D10)</option>
                    <option value="12">12-sided (D12)</option>
                    <option value="20">20-sided (D20)</option>
                    <option value="100">100-sided (D100)</option>
                  </select>
                </div>
              )}

              {generationMethod === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Custom Values (comma-separated)
                  </label>
                  <textarea
                    value={customValues}
                    onChange={(e) => setCustomValues(e.target.value)}
                    placeholder="1, 5, 10, 25, 50, 100"
                    rows={3}
                    className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  />
                </div>
              )}

              {generationMethod === 'gaussian' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Mean (μ)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={minValue}
                      onChange={(e) => setMinValue(e.target.value)}
                      placeholder="0"
                      className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Std Dev (σ)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={maxValue}
                      onChange={(e) => setMaxValue(e.target.value)}
                      placeholder="1"
                      className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                  </div>
                </div>
              )}

              <div className="border-t border-gray-600 pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Seed (for reproducible results)
                  </label>
                  <input
                    type="text"
                    value={seed}
                    onChange={(e) => setSeed(e.target.value)}
                    placeholder="Leave empty for random seed"
                    className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  />
                </div>
              </div>

              <div className="border-t border-gray-600 pt-4 space-y-2">
                {generationMethod !== 'coin' && (
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-300">
                      Allow Duplicates
                    </label>
                    <button
                      onClick={() => setAllowDuplicates(!allowDuplicates)}
                      className={`px-3 py-1 rounded text-sm transition-colors ${
                        allowDuplicates ? 'bg-purple-600 text-white' : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      {allowDuplicates ? 'ON' : 'OFF'}
                    </button>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Show Statistics
                  </label>
                  <button
                    onClick={() => setShowStatistics(!showStatistics)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showStatistics ? 'bg-purple-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showStatistics ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Show Distribution
                  </label>
                  <button
                    onClick={() => setShowDistribution(!showDistribution)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showDistribution ? 'bg-purple-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showDistribution ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Generated Numbers</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!result}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadNumbers}
                  disabled={!result}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-y-auto min-h-[500px]">
              {result ? (
                <div className="space-y-4">
                  <div className="text-center border-b border-gray-600 pb-4">
                    <div className="text-2xl font-bold text-purple-400 mb-2">
                      {result.count} Numbers Generated
                    </div>
                    <div className="text-gray-400 text-sm">
                      Method: {result.method} | 
                      Time: {result.processingTime.toFixed(2)}ms |
                      Generated: {result.timestamp.toLocaleString()}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-white font-medium mb-2">Random Numbers:</h4>
                    <div className="bg-gray-700 p-3 rounded font-mono text-sm text-green-400 max-h-40 overflow-y-auto">
                      {result.numbers.join(', ')}
                    </div>
                  </div>
                  
                  {result.statistics && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Statistical Analysis:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm space-y-1">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <div className="text-blue-400">Minimum: <span className="text-white font-mono">{result.statistics.min}</span></div>
                            <div className="text-blue-400">Maximum: <span className="text-white font-mono">{result.statistics.max}</span></div>
                            <div className="text-blue-400">Range: <span className="text-white font-mono">{result.statistics.range}</span></div>
                            <div className="text-blue-400">Mean: <span className="text-white font-mono">{result.statistics.mean}</span></div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-yellow-400">Median: <span className="text-white font-mono">{result.statistics.median}</span></div>
                            <div className="text-yellow-400">Mode: <span className="text-white font-mono">{result.statistics.mode.join(', ')}</span></div>
                            <div className="text-yellow-400">Std Dev: <span className="text-white font-mono">{result.statistics.standardDeviation}</span></div>
                            <div className="text-yellow-400">Variance: <span className="text-white font-mono">{result.statistics.variance}</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {result.distribution && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Number Distribution:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm max-h-32 overflow-y-auto">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {Object.entries(result.distribution)
                            .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
                            .map(([value, frequency]) => (
                            <div key={value} className="text-purple-400 font-mono text-xs">
                              {value}: <span className="text-white">{frequency}×</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <Shuffle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Configure parameters to generate random numbers</p>
                    <p className="text-sm mt-2">Choose generation method and set values above</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History Section */}
        {history.length > 0 && (
          <div className="mt-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Generations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-40 overflow-y-auto">
              {history.slice(0, 6).map((gen, index) => (
                <div key={index} className="bg-gray-700 p-3 rounded">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-white">
                      {gen.method} ({gen.count} numbers)
                    </div>
                    <div className="text-xs text-gray-400">
                      {gen.processingTime.toFixed(1)}ms
                    </div>
                  </div>
                  <div className="text-xs font-mono text-green-400 truncate">
                    {gen.numbers.slice(0, 10).join(', ')}
                    {gen.numbers.length > 10 ? '...' : ''}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {gen.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Information Section */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Random Number Generation Methods</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-purple-400 mb-2 flex items-center">
                <Hash className="w-4 h-4 mr-1" />
                Uniform Distribution
              </h4>
              <p className="text-gray-300">
                Range and Dice methods use uniform distribution where each possible value has equal probability of being selected. Perfect for fair randomization.
              </p>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-yellow-400 mb-2">Gaussian (Normal) Distribution</h4>
              <p className="text-gray-300">
                Values cluster around the mean (μ) with spread determined by standard deviation (σ). About 68% of values fall within 1σ of the mean.
              </p>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-green-400 mb-2">Seeded Generation</h4>
              <p className="text-gray-300">
                Using a seed makes randomization reproducible. Same seed always produces the same sequence, useful for testing and debugging.
              </p>
            </div>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-blue-400 mb-2">Statistical Measures</h4>
              <div className="text-gray-300 space-y-1">
                <div><strong>Mean:</strong> Average of all values</div>
                <div><strong>Median:</strong> Middle value when sorted</div>
                <div><strong>Mode:</strong> Most frequently occurring value(s)</div>
                <div><strong>Standard Deviation:</strong> Measure of spread from mean</div>
              </div>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-red-400 mb-2">Use Cases</h4>
              <div className="text-gray-300 space-y-1">
                <div><strong>Gaming:</strong> Dice rolls, card shuffling</div>
                <div><strong>Testing:</strong> Sample data generation</div>
                <div><strong>Simulation:</strong> Monte Carlo methods</div>
                <div><strong>Cryptography:</strong> Nonce and key generation</div>
              </div>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RandomNumberGenerator;